<template>
    <div class="rd">
        <div class="pagina">
            <div v-if="booking">
                <header class="paginakop">
                    <div class="tekst">
                        <nav class="kruimel" aria-label="Breadcrumb">
                            <router-link :to="{ name: 'bookings' }">Bookings</router-link>
                            <span>/</span>
                            <router-link :to="{ name: 'view-booking', params: { id: booking.id } }" class="num">{{ booking.code }}</router-link>
                            <span>/</span>
                            <span>Move</span>
                        </nav>
                        <h1>Move booking</h1>
                        <p>{{ booking.experience.name }} · {{ booking.xcube.location.name }} · {{ booking.xcube.name }}</p>
                    </div>
                    <div class="rechts">
                        <router-link class="btn btn-stil btn-sm" :to="{ name: 'view-booking', params: { id: booking.id } }">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
                            Back to booking
                        </router-link>
                    </div>
                </header>

                <div class="paneel" style="max-width: 720px">
                    <div class="melding-blok info" role="note">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                        <div>Currently on <b>{{ dayjs(booking.date).tz(locationTimeZone).format("ddd D MMM YYYY, HH:mm") }}</b>. Pick a new day and a free start time. The customer gets an updated door code by email.</div>
                    </div>

                    <div class="filter-rij">
                        <div class="veld" style="flex: 0 0 170px">
                            <label for="mb-datum">Date</label>
                            <input id="mb-datum" v-model="eventDateStr" type="date" :min="todayStr" />
                        </div>
                        <div class="snel" style="padding-bottom: 2px">
                            <button v-for="d in quickDates" :key="d.value" class="chip" type="button" :aria-pressed="eventDateStr === d.value" @click="pickDate(d.value)">{{ d.label }}</button>
                        </div>
                    </div>

                    <p v-if="selectedDayOutside" class="hint">This day is outside the location's opening hours. Moving the booking here will be refused.</p>

                    <div v-if="slotsWithStatus.length" class="slots" role="group" aria-label="Start time">
                        <button v-for="s in slotsWithStatus" :key="s.time" type="button" :disabled="s.status === 'past' || s.status === 'unavailable'" :aria-pressed="s.status === 'selected'" @click="pickTime(s.time)">{{ s.time }}</button>
                    </div>
                    <div v-else class="leeg">
                        <h2>No free times on this day</h2>
                        <p>Pick another date.</p>
                    </div>

                    <div class="slot-legenda">
                        <span><i></i>Free</span>
                        <span><i class="bezet"></i>Taken or in the past</span>
                        <span><i class="gekozen"></i>Selected</span>
                        <span>Timezone: {{ locationTimeZone }}</span>
                    </div>
                    <p v-if="selectedTimeOutside" class="hint" style="color: var(--rood); font-weight: 600">This time is outside the location's opening hours. Moving the booking here will be refused.</p>
                    <p v-if="eventDateTime && locationTimeZone !== userTimeZone" class="hint">Local time: {{ dayjs(eventDateTime).format("DD-MM-YYYY HH:mm") }} {{ userTimeZone }}</p>

                    <div class="knoprij">
                        <button class="btn btn-pink" type="button" :disabled="!eventTime || loading" @click="handleMoveBooking">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /><path d="m16 4 3 1-1 3" /></svg>
                            {{ loading ? "Moving booking…" : "Move booking" }}
                        </button>
                        <router-link class="btn btn-stil" :to="{ name: 'view-booking', params: { id: booking.id } }">Cancel</router-link>
                    </div>
                </div>
            </div>
            <div v-else class="leeg"><h2>Loading booking…</h2></div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import dayjs from "dayjs";
import { Message } from "element-ui";

import { getLocationSchedule, moveBooking, getTimeSlots } from "../../api/rest";
import { calendarDateOf, dayIsOutsideOpeningHours, timeIsOutsideOpeningHours } from "../../helpers/openingHours";
import { fetchBookingData } from "../../api/graphql/bookings";
import { Temporal } from "temporal-polyfill";

const router = useRouter();
const route = useRoute();

const loading = ref(false);
const timeSlots = ref([]);
const schedule = ref(null);

const eventDate = ref(new Date());
const eventTime = ref("");

const booking = ref(null);

const userTimeZone = computed(() => {
    // Get the current timezone name
    const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezoneName;
});

const locationTimeZone = computed(() => booking.value.xcube.location.time_zone);

const selectedDayOutside = computed(() => {
    if (!schedule.value || !eventDate.value || !booking.value) {
        return false;
    }
    return dayIsOutsideOpeningHours(schedule.value, calendarDateOf(eventDate.value), locationTimeZone.value);
});

const selectedTimeOutside = computed(() => {
    if (selectedDayOutside.value || !schedule.value || !eventTime.value || !booking.value) {
        return false;
    }
    return timeIsOutsideOpeningHours(schedule.value, {
        date: calendarDateOf(eventDate.value),
        time: eventTime.value,
        duration: booking.value.experience.duration,
        timeZone: locationTimeZone.value,
    });
});

const slotsWithStatus = computed(() => timeSlots.value.map((slot) => {
    let status = "available";
    const [hours, minutes] = slot.time.split(":").map(Number);
    const slotDateTime = dayjs(eventDate.value).tz(locationTimeZone.value, true)
        .hour(hours)
        .minute(minutes)
        .second(0);
    if (slotDateTime.isBefore(dayjs())) {
        status = "past";
    } else if (slot.available === 0) {
        status = "unavailable";
    } else if (slot.time === eventTime.value) {
        status = "selected";
    }
    return {
        time: slot.time,
        status,
    };
}));

const eventDateTime = computed(() => {
    if (eventDate.value && eventTime.value) {
        let realDate = eventDate.value;
        if (eventTime.value > "00:00" && eventTime.value < "02:00") {
            realDate = dayjs(realDate).add(1, "day").toDate();
        }
        const zdt = Temporal.ZonedDateTime.from({
            timeZone: locationTimeZone.value,
            year: realDate.getFullYear(),
            month: realDate.getMonth() + 1,
            day: realDate.getDate(),
            hour: Number.parseInt(eventTime.value.split(":")[0]),
            minute: Number.parseInt(eventTime.value.split(":")[1]),
            second: 0
        });
        return new Date(zdt.epochMilliseconds);
    }
    return null;
});

watch(eventDate, async () => {
    if (booking.value) {
        const { json, status } = await getTimeSlots(
            booking.value.xcube.id,
            dayjs(eventDate.value).format("YYYY-MM-DD"),
            booking.value.experience.duration,
            booking.value.id,
        )
        if (status !== 200) {
            Message.error({
                message: `Error from server : ${json.message}`,
            });
            return;
        }
        timeSlots.value = json;
    }
},
);

const todayStr = dayjs().format("YYYY-MM-DD");

const eventDateStr = computed({
    get: () => (eventDate.value ? dayjs(eventDate.value).format("YYYY-MM-DD") : ""),
    set: (value) => {
        if (value && value >= todayStr) {
            eventDate.value = dayjs(value).toDate();
            eventTime.value = "";
        }
    },
});

const quickDates = computed(() =>
    [0, 1, 2].map((offset) => {
        const d = dayjs().add(offset, "day");
        return { value: d.format("YYYY-MM-DD"), label: offset === 0 ? "Today" : offset === 1 ? "Tomorrow" : d.format("ddd D MMM") };
    }),
);

const pickDate = (value) => {
    eventDateStr.value = value;
};
const pickTime = (time) => {
    eventTime.value = time;
};

const handleMoveBooking = async () => {
    if (dayjs(eventDateTime.value).isBefore(dayjs())) {
        Message.error({
            message: "Date/time should be in the future",
        });
        return;
    }
    console.log("NEW DATE", eventDateTime.value);
    try {
        loading.value = true;
        const { data, status } = await moveBooking(booking.value, eventDateTime.value);
        if (status !== 200) {
            throw new Error(data.message);
        }
        console.log("NEW BOOKING", data);
        loading.value = false;
        Message.success({ message: "Booking has been moved" });
        router.push({
            name: "view-booking",
            params: { id: data.id },
        });
    } catch (e) {
        loading.value = false;
        if (e.message === "conflict") {
            Message.error({
                message: "There is already another booking on this time",
            });
        } else {
            Message.error({
                message: `Error from server : ${e.message}`,
            });
            throw e;
        }
    }
};

onMounted(async () => {
    booking.value = await fetchBookingData(route.params.id);
    try {
        schedule.value = await getLocationSchedule(booking.value.xcube.location.id);
    } catch (error) {
        schedule.value = null;
        Message.error(error.message || "Could not load opening hours");
    }
    eventDate.value = new Date(booking.value.date);
    const bookingDate = dayjs(booking.value.date).tz(locationTimeZone.value);
    const minutes = bookingDate.minute();
    const roundedMinutes = Math.round(minutes / 15) * 15;
    eventTime.value = `${bookingDate.hour().toString().padStart(2,"0")}:${roundedMinutes.toString().padStart(2, "0")}`;
    eventDate.value = new Date(booking.value.date);
});
</script>